import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeaturedVideos from '../components/FeaturedVideos';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SupportModal from '../components/SupportModal';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import logoImage from '../assets/animlearn-logo.png';
import { authService } from '../services/authService';
import { getMyRole } from '../services/studioApi';
import {
  LogOut, Home, Upload, Brain, Video, BookOpen, CheckCircle,
  GraduationCap, Zap, BarChart2, Users, FileText,
  PlayCircle, Star, ChevronRight, Sparkles, Globe, Target,
  ClipboardList, TrendingUp, MessageSquare, Layers, Award
} from 'lucide-react';
import './HomePage.css';

export default function HomePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(authService.getUser()?.role || null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [navbarHidden, setNavbarHidden] = useState(false);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const lastScrollY = useRef(0);

  const PROMO_VIDEOS = {
    en: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/992ca94d-92a7-40b4-b026-68112c58d6df.mp4',
    fi: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/Untitled%20copy%20%E2%80%91%20Made%20with%20FlexClip.mp4',
    sv: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/992ca94d-92a7-40b4-b026-68112c58d6df.mp4'
  };
  const PROMO_VIDEO_URL = PROMO_VIDEOS[language] || PROMO_VIDEOS.en;

  const PROMO_THUMBNAILS = {
    en: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/Promo_thumbnail_1_72.6s.png',
    fi: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/video-capture-t0087.72seg-8315.png',
    sv: 'https://pub-139eb053b91a419893ac1c1a94f3ffd5.r2.dev/Promo/Promo_thumbnail_1_72.6s.png',
  };
  const PROMO_THUMBNAIL_URL = PROMO_THUMBNAILS[language] || PROMO_THUMBNAILS.en;

  useEffect(() => {
    setIsVideoPlaying(false);
    setIsAutoPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    const timer = setTimeout(() => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) setIsAutoPlaying(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [language]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { if (!isVideoPlaying) setIsAutoPlaying(true); }
        else { setIsAutoPlaying(false); if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; } }
      },
      { threshold: 0.4 }
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, [isVideoPlaying]);

  useEffect(() => {
    const token = authService.getToken();
    const user = authService.getUser();
    if (token && user) {
      setIsLoggedIn(true);
      getMyRole().then(({ role }) => {
        if (role) { setUserRole(role); authService.saveAuth(token, { ...user, role }); }
      }).catch(() => {});
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  // Scroll: hide navbar on down, show on up; add glass effect after 60px
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setNavbarScrolled(currentY > 60);
      if (currentY > lastScrollY.current && currentY > 100) setNavbarHidden(true);
      else setNavbarHidden(false);
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll-reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const ht = t.homepage || {};
  const isStudentOrTeacher = userRole === 'student' || userRole === 'teacher';

  const handleGoogleSignIn = () => { window.location.href = '/api/auth-google'; };
  const handleEmailSignIn = () => navigate('/signin');
  const handleSignUp = () => navigate('/signup');
  const handleGoToDashboard = () => {
    if (authService.isAuthenticated()) {
      if (userRole === 'student') navigate('/studio/student');
      else if (userRole === 'teacher') navigate('/studio/teacher');
      else navigate('/dashboard');
    } else navigate('/signin');
  };
  const handleLogout = () => { authService.logout(); setIsLoggedIn(false); window.location.href = '/'; };

  return (
    <div className="home-page">

      {/* ── NAVBAR ── */}
      <nav className={`navbar${navbarScrolled ? ' navbar-scrolled' : ''}${navbarHidden ? ' navbar-hidden' : ''}`}>
        <div className="nav-container">
          <div className="logo" onClick={() => navigate('/')}>
            <img src={logoImage} alt="AnimLearn" className="logo-image" />
          </div>

          <div className="nav-menu">
            <a href="#for-who" className="nav-link">{ht.nav?.forWho}</a>
            <a href="#how-it-works" className="nav-link">{ht.nav?.howItWorks}</a>
            <a href="#features" className="nav-link">{ht.nav?.features}</a>
            <a href="#videos" className="nav-link">{ht.nav?.seeItLive}</a>
            <a href="#founders" className="nav-link">{ht.nav?.team}</a>
          </div>

          <div className="nav-buttons">
            {authService.isAuthenticated() && !isStudentOrTeacher && (
              <button onClick={handleGoToDashboard} className="btn-nav-dashboard">
                <Home size={16} /><span>{t.dashboard}</span>
              </button>
            )}
            <LanguageSwitcher />
            {!isLoggedIn
              ? <button onClick={handleEmailSignIn} className="btn-nav-signin"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg><span className="nav-btn-text">{t.signIn}</span></button>
              : <button onClick={handleLogout} className="btn-nav-logout"><LogOut size={16} /><span className="nav-btn-text">{t.logout}</span></button>
            }
            <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              <span /><span /><span />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="mobile-menu">
            {[
              ['#for-who', ht.nav?.forWho],
              ['#how-it-works', ht.nav?.howItWorks],
              ['#features', ht.nav?.features],
              ['#videos', ht.nav?.seeItLive],
              ['#founders', ht.nav?.team],
            ].map(([href, label]) => (
              <a key={href} href={href} className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>{label}</a>
            ))}
            <a href="#get-started" className="mobile-nav-link mobile-cta" onClick={() => setIsMobileMenuOpen(false)}>{ht.nav?.getStarted}</a>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg-shapes">
          <div className="shape shape-1" />
          <div className="shape shape-2" />
          <div className="shape shape-3" />
        </div>

        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-badge reveal">
              <Sparkles size={14} />
              <span>{ht.hero?.badge}</span>
            </div>

            <h1 className="hero-title reveal">
              {isStudentOrTeacher ? (
                <>{ht.hero?.titleLoggedIn}</>
              ) : (
                <>{ht.hero?.titleLine1}<br/><span className="grad-text">{ht.hero?.titleBuild}</span> · {ht.hero?.titleLine2}<br/><span className="grad-teal">{ht.hero?.titleMaster}</span></>
              )}
            </h1>

            <p className="hero-subtitle reveal">{ht.hero?.subtitle}</p>

            <div className="hero-pills reveal">
              <span className="pill">{ht.hero?.pill1}</span>
              <span className="pill">{ht.hero?.pill2}</span>
              <span className="pill">{ht.hero?.pill3}</span>
              <span className="pill">{ht.hero?.pill4}</span>
            </div>

            <div className="hero-ctas reveal">
              {!isLoggedIn ? (
                <>
                  <button onClick={handleGoogleSignIn} className="btn-hero-google">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {ht.hero?.googleBtn}
                  </button>
                  <button onClick={handleSignUp} className="btn-hero-primary">
                    {ht.hero?.startBtn} <ChevronRight size={16} />
                  </button>
                </>
              ) : (
                <button onClick={handleGoToDashboard} className="btn-hero-primary">
                  {t.goToDashboard} <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="hero-visual">
            <div className="phone-mockup-wrapper">
              <div className="phone-mockup">
                <div className="phone-top">
                  <div className="phone-camera" />
                  <div className="phone-speaker" />
                </div>
                <div className="phone-screen">
                  {isVideoPlaying ? (
                    <div className="promo-player">
                      <video ref={videoRef} src={PROMO_VIDEO_URL} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button className="close-video-btn" onClick={() => { setIsVideoPlaying(false); setIsAutoPlaying(true); }}>✕</button>
                    </div>
                  ) : isAutoPlaying ? (
                    <div className="promo-thumbnail promo-autoplaying" onClick={() => { setIsAutoPlaying(false); setIsVideoPlaying(true); }}>
                      <video ref={videoRef} src={PROMO_VIDEO_URL} autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <div className="thumbnail-hover-overlay">
                        <div className="play-button"><svg viewBox="0 0 24 24" fill="white" width="40" height="40"><path d="M8 5v14l11-7z"/></svg></div>
                        <span className="promo-label">{ht.hero?.playSound}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="promo-thumbnail" onClick={() => setIsVideoPlaying(true)}>
                      {PROMO_THUMBNAIL_URL
                        ? <img src={PROMO_THUMBNAIL_URL} alt="AnimLearn demo" className="thumbnail-img" />
                        : <div className="thumbnail-placeholder"><div className="thumbnail-icon">🎬</div><p>See AnimLearn in Action</p></div>
                      }
                      <div className="thumbnail-hover-overlay">
                        <div className="play-button"><svg viewBox="0 0 24 24" fill="white" width="40" height="40"><path d="M8 5v14l11-7z"/></svg></div>
                        <span className="promo-label">{ht.hero?.watchDemo}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="phone-bottom"><div className="phone-home-indicator" /></div>
              </div>
              <div className="phone-glow" />

              {/* Floating badges */}
              <div className="float-badge float-badge-1"><Star size={14} fill="currentColor" /> 4.9 Rating</div>
              <div className="float-badge float-badge-2"><CheckCircle size={14} /> Syllabus aligned</div>
              <div className="float-badge float-badge-3"><Zap size={14} /> 60s videos</div>
            </div>
          </div>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none"><path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#ffffff"/></svg>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <section className="stats-strip">
        {[
          { icon: <Users size={22}/>, num: '3', label: ht.stats?.subjects },
          { icon: <Video size={22}/>, num: 'AI', label: ht.stats?.videos },
          { icon: <Brain size={22}/>, num: '∞', label: ht.stats?.problems },
          { icon: <Globe size={22}/>, num: '3', label: ht.stats?.languages },
          { icon: <BarChart2 size={22}/>, num: 'Live', label: ht.stats?.analytics },
        ].map((s, i) => (
          <div key={i} className="stat-item reveal">
            <div className="stat-icon">{s.icon}</div>
            <div className="stat-num">{s.num}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      {/* ── FOR WHO ── */}
      <section className="for-who" id="for-who">
        <div className="section-header reveal">
          <h2>{ht.forWho?.titleNormal} <span className="grad-text">{ht.forWho?.titleHighlight}</span></h2>
          <p>{ht.forWho?.subtitle}</p>
        </div>

        <div className="audience-cards">
          {/* Teacher card */}
          <div className="audience-card teacher-card reveal">
            <div className="audience-card-header">
              <div className="audience-icon"><GraduationCap size={32} /></div>
              <h3>{ht.forWho?.teacherTitle}</h3>
              <p>{ht.forWho?.teacherDesc}</p>
            </div>
            <ul className="audience-features">
              <li><CheckCircle size={16} /><span>{ht.forWho?.teacherF1}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.teacherF2}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.teacherF3}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.teacherF4}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.teacherF5}</span></li>
            </ul>
            <button className="audience-cta teacher-cta" onClick={isLoggedIn ? handleGoToDashboard : handleSignUp}>
              {ht.forWho?.teacherCta} <ChevronRight size={16} />
            </button>
          </div>

          {/* Student card */}
          <div className="audience-card student-card reveal">
            <div className="audience-card-header">
              <div className="audience-icon"><BookOpen size={32} /></div>
              <h3>{ht.forWho?.studentTitle}</h3>
              <p>{ht.forWho?.studentDesc}</p>
            </div>
            <ul className="audience-features">
              <li><CheckCircle size={16} /><span>{ht.forWho?.studentF1}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.studentF2}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.studentF3}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.studentF4}</span></li>
              <li><CheckCircle size={16} /><span>{ht.forWho?.studentF5}</span></li>
            </ul>
            <button className="audience-cta student-cta" onClick={isLoggedIn ? handleGoToDashboard : handleSignUp}>
              {ht.forWho?.studentCta} <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-it-works" id="how-it-works">
        <div className="hiw-bg" />
        <div className="hiw-inner">
          <div className="section-header reveal">
            <h2>{t.howItWorks?.split(' ').slice(0,-1).join(' ')} <span className="grad-text">{t.howItWorks?.split(' ').slice(-1)}</span></h2>
            <p>{ht.hiw?.subtitle}</p>
          </div>

          <div className="hiw-steps">
            {[
              { num: '01', icon: <Upload size={28}/>, title: ht.hiw?.step1Title, desc: ht.hiw?.step1Desc, color: '#8B5CF6' },
              { num: '02', icon: <Video size={28}/>, title: ht.hiw?.step2Title, desc: ht.hiw?.step2Desc, color: '#0DBFB0' },
              { num: '03', icon: <ClipboardList size={28}/>, title: ht.hiw?.step3Title, desc: ht.hiw?.step3Desc, color: '#F59E0B' },
              { num: '04', icon: <TrendingUp size={28}/>, title: ht.hiw?.step4Title, desc: ht.hiw?.step4Desc, color: '#EF4444' },
            ].map((step, i) => (
              <div key={i} className="hiw-step reveal">
                <div className="hiw-step-num" style={{ color: step.color, borderColor: step.color + '40' }}>{step.num}</div>
                <div className="hiw-step-icon" style={{ background: step.color + '15', color: step.color }}>{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < 3 && <div className="hiw-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TEACHER FEATURES ── */}
      <section className="features-section teacher-section" id="features">
        <div className="features-inner">
          <div className="features-label reveal">{ht.teacherFeatures?.label}</div>
          <div className="section-header reveal">
            <h2>{ht.teacherFeatures?.titlePart1}<br/><span className="grad-text">{ht.teacherFeatures?.titlePart2}</span></h2>
            <p>{ht.teacherFeatures?.subtitle}</p>
          </div>

          <div className="feature-cards">
            {[
              { icon: <FileText size={32}/>, color: '#8B5CF6', bg: '#F3EEFF', title: ht.teacherFeatures?.f1Title, desc: ht.teacherFeatures?.f1Desc },
              { icon: <Video size={32}/>, color: '#0DBFB0', bg: '#E6FAF9', title: ht.teacherFeatures?.f2Title, desc: ht.teacherFeatures?.f2Desc },
              { icon: <ClipboardList size={32}/>, color: '#F59E0B', bg: '#FEF3C7', title: ht.teacherFeatures?.f3Title, desc: ht.teacherFeatures?.f3Desc },
              { icon: <BarChart2 size={32}/>, color: '#EF4444', bg: '#FEE2E2', title: ht.teacherFeatures?.f4Title, desc: ht.teacherFeatures?.f4Desc },
              { icon: <Users size={32}/>, color: '#6366F1', bg: '#EEF2FF', title: ht.teacherFeatures?.f5Title, desc: ht.teacherFeatures?.f5Desc },
              { icon: <Globe size={32}/>, color: '#EC4899', bg: '#FCE7F3', title: ht.teacherFeatures?.f6Title, desc: ht.teacherFeatures?.f6Desc },
            ].map((f, i) => (
              <div key={i} className="feature-card reveal">
                <div className="feature-card-icon" style={{ background: f.bg, color: f.color }}>{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STUDENT FEATURES ── */}
      <section className="features-section student-section">
        <div className="features-inner">
          <div className="features-label reveal" style={{ background: '#E6FAF9', color: '#0DBFB0' }}>{ht.studentFeatures?.label}</div>
          <div className="section-header reveal">
            <h2>{ht.studentFeatures?.titlePart1}<br/><span className="grad-teal">{ht.studentFeatures?.titlePart2}</span></h2>
            <p>{ht.studentFeatures?.subtitle}</p>
          </div>

          <div className="student-showcase">
            <div className="student-feature-list">
              {[
                { icon: <PlayCircle size={24}/>, color: '#8B5CF6', title: ht.studentFeatures?.f1Title, desc: ht.studentFeatures?.f1Desc },
                { icon: <Brain size={24}/>, color: '#0DBFB0', title: ht.studentFeatures?.f2Title, desc: ht.studentFeatures?.f2Desc },
                { icon: <Zap size={24}/>, color: '#F59E0B', title: ht.studentFeatures?.f3Title, desc: ht.studentFeatures?.f3Desc },
                { icon: <ClipboardList size={24}/>, color: '#EF4444', title: ht.studentFeatures?.f4Title, desc: ht.studentFeatures?.f4Desc },
                { icon: <TrendingUp size={24}/>, color: '#6366F1', title: ht.studentFeatures?.f5Title, desc: ht.studentFeatures?.f5Desc },
                { icon: <MessageSquare size={24}/>, color: '#EC4899', title: ht.studentFeatures?.f6Title, desc: ht.studentFeatures?.f6Desc },
              ].map((f, i) => (
                <div key={i} className="student-feature-item reveal">
                  <div className="sfi-icon" style={{ background: f.color + '15', color: f.color }}>{f.icon}</div>
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="student-visual reveal">
              <div className="student-card-stack">
                <div className="scard scard-mastery">
                  <div className="scard-label"><TrendingUp size={14}/> {ht.studentFeatures?.masteryTitle || 'Your Mastery'}</div>
                  <div className="scard-bars">
                    {[92, 78, 65, 55].map((pct, i) => (
                      <div key={i} className="scard-bar-row">
                        <span>{(ht.studentFeatures?.masteryTopics || ['Photosynthesis','Cell Division','Genetics','Evolution'])[i]}</span>
                        <div className="scard-bar"><div className="scard-bar-fill" style={{ width: `${pct}%`, background: pct > 80 ? '#22c55e' : pct > 65 ? '#eab308' : '#f97316' }} /></div>
                        <span className="scard-pct">{pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="scard scard-reaction">
                  <div className="scard-label">{ht.studentFeatures?.reactionLabel || 'How did you find this video?'}</div>
                  <div className="scard-smileys">
                    {[['😄','#22c55e'], ['🙂','#eab308'], ['😕','#f97316'], ['😟','#ef4444']].map(([e,c]) => (
                      <div key={e} className="scard-smiley" style={{ borderColor: c + '60', background: c + '15' }}>{e}</div>
                    ))}
                  </div>
                </div>

                <div className="scard scard-practice">
                  <div className="scard-label"><Zap size={14}/> {ht.studentFeatures?.adaptiveTitle || 'Adaptive Practice'}</div>
                  <div className="scard-q">{ht.studentFeatures?.adaptiveQuestion || 'What is the role of chlorophyll in photosynthesis?'}</div>
                  <div className="scard-options">
                    {(ht.studentFeatures?.adaptiveOptions || ['Absorb light energy','Store glucose','Transport water','Produce oxygen']).map((o,i) => (
                      <div key={i} className={`scard-opt${i===0?' scard-opt-correct':''}`}>{o}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PIXEL ACADEMY ── */}
      <section className="pixel-academy-section">
        <div className="pa-inner reveal">
          <div className="pa-badge"><Award size={16}/> {ht.pixelAcademy?.badge}</div>
          <h2>{ht.pixelAcademy?.title} <span className="pa-brand">{ht.pixelAcademy?.brand}</span></h2>
          <p>{ht.pixelAcademy?.desc}</p>
          <div className="pa-features">
            {[
              { icon: <Layers size={20}/>, text: ht.pixelAcademy?.f1 },
              { icon: <Users size={20}/>, text: ht.pixelAcademy?.f2 },
              { icon: <BarChart2 size={20}/>, text: ht.pixelAcademy?.f3 },
              { icon: <Target size={20}/>, text: ht.pixelAcademy?.f4 },
            ].map((f, i) => (
              <div key={i} className="pa-feature"><div className="pa-feature-icon">{f.icon}</div><span>{f.text}</span></div>
            ))}
          </div>
          <button className="pa-cta" onClick={isLoggedIn ? handleGoToDashboard : handleSignUp}>
            {ht.pixelAcademy?.cta} <ChevronRight size={16} />
          </button>
        </div>
      </section>

      {/* ── SUBJECTS ── */}
      <section className="subjects-section" id="subjects">
        <div className="section-header reveal">
          <h2>{ht.subjects?.title} <span className="grad-text">{ht.subjects?.titleHighlight}</span></h2>
          <p>{ht.subjects?.subtitle}</p>
        </div>
        <div className="subjects-grid">
          {[
            { emoji: '🧪', name: t.chemistry, color: '#8B5CF6', bg: '#F3EEFF', topics: [t.chemistryTopics?.molecular, t.chemistryTopics?.reactions, t.chemistryTopics?.ph, t.chemistryTopics?.electro] },
            { emoji: '⚛️', name: t.physics, color: '#0DBFB0', bg: '#E6FAF9', topics: [t.physicsTopics?.motion, t.physicsTopics?.energy, t.physicsTopics?.waves, t.physicsTopics?.electricity] },
            { emoji: '📐', name: t.mathematics, color: '#F59E0B', bg: '#FEF3C7', topics: [t.mathTopics?.calculus, t.mathTopics?.geometry, t.mathTopics?.probability, t.mathTopics?.functions] },
          ].map((s, i) => (
            <div key={i} className="subject-card reveal" style={{ '--s-color': s.color, '--s-bg': s.bg }}>
              <div className="subject-emoji">{s.emoji}</div>
              <h3>{s.name}</h3>
              <ul>{s.topics.map(t => <li key={t}>{t}</li>)}</ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURED VIDEOS ── */}
      <div id="videos"><FeaturedVideos /></div>

      {/* ── TEAM ── */}
      <section className="founders-section" id="founders">
        <div className="founders-inner">
          <div className="section-header reveal">
            <h2>{t.team?.title || 'Meet the Team'}</h2>
            <p>{t.team?.subtitle || 'The people building the future of visual learning'}</p>
          </div>

          <div className="founders-grid">
            {[
              { initials: 'SP', name: 'Saravanakumar Ponnuswamy', role: t.team?.founder1Role || 'Co-Founder & CEO', bio: t.team?.founder1Bio || 'Building AI-powered education tools to make learning visual and accessible.', linkedin: 'https://www.linkedin.com/in/saravanakumarponnuswamy', color: '#8B5CF6' },
              { initials: 'NA', name: 'Neha Adhvika', role: t.team?.founder2Role || 'Co-Founder & COO', bio: t.team?.founder2Bio || 'Designing learning experiences that connect curriculum with creativity.', linkedin: 'https://www.linkedin.com/in/neha-adhvika-s-09a1b237a/', color: '#0DBFB0' },
            ].map((f, i) => (
              <div key={i} className="founder-card reveal">
                <div className="founder-avatar" style={{ background: `linear-gradient(135deg, ${f.color}, ${f.color}aa)` }}>
                  <span>{f.initials}</span>
                </div>
                <div className="founder-info">
                  <h3>{f.name}</h3>
                  <p className="founder-role">{f.role}</p>
                  <p className="founder-bio">{f.bio}</p>
                  <a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="linkedin-btn">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    {t.team?.linkedIn || 'LinkedIn'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section" id="get-started">
        <div className="cta-inner reveal">
          <div className="cta-badge"><Star size={14} fill="currentColor" /> {ht.cta?.badge}</div>
          <h2>{ht.cta?.title}</h2>
          <p>{ht.cta?.subtitle}</p>
          <div className="cta-buttons">
            {!isLoggedIn ? (
              <>
                <button onClick={handleGoogleSignIn} className="btn-cta-google">
                  <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  {ht.cta?.googleBtn}
                </button>
                <button onClick={handleSignUp} className="btn-cta-primary">{ht.cta?.primaryBtn} <ChevronRight size={16}/></button>
              </>
            ) : (
              <button onClick={handleGoToDashboard} className="btn-cta-primary">{t.goToDashboard} <ChevronRight size={16}/></button>
            )}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={logoImage} alt="AnimLearn" style={{ height: 44 }} />
            <p>{ht.footer?.tagline}</p>
            <p className="footer-company">{ht.footer?.company}</p>
            <p className="footer-business-id">{ht.footer?.businessId}</p>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <h4>{ht.footer?.product}</h4>
              <a href="#features">{ht.nav?.features}</a>
              <a href="#subjects">{ht.subjects?.title}</a>
              <a href="#how-it-works">{ht.nav?.howItWorks}</a>
              <a href="#for-who">{ht.nav?.forWho}</a>
            </div>
            <div className="footer-col">
              <h4>{ht.footer?.support}</h4>
              <button onClick={() => setShowSupportModal(true)} className="footer-email-btn">hello@animlearn.com</button>
            </div>
            <div className="footer-col">
              <h4>{ht.footer?.legal}</h4>
              <a href="/privacy-policy">{ht.footer?.privacyPolicy}</a>
              <a href="/terms-of-service">{ht.footer?.termsOfService}</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{ht.footer?.copyright} &nbsp;·&nbsp; {ht.footer?.company} &nbsp;·&nbsp; {ht.footer?.businessId}</p>
          <p>{t.footerDisclaimerText || 'AI-generated solutions should be verified by qualified educators.'}</p>
        </div>
      </footer>

      <SupportModal isOpen={showSupportModal} onClose={() => setShowSupportModal(false)} isAuthenticated={isLoggedIn} />
    </div>
  );
}
